/* Static fixtures for MSW handlers.
   Typed against api/schemas.ts — the mocks cannot drift from the real
   contract. When the real backend is wired in Phase 1, MSW intercepts
   stop firing (VITE_USE_MOCKS=false) and the SPA hits APIM instead. */

import type {
  PreflightAuthResult,
  RunAgentResponse,
  ToolResponse,
} from '../schemas';

export const MOCK_USER = {
  oid: '00000000-0000-0000-0000-000000000001',
  name: 'Rachel Torres',
  email: 'rachel.torres@syncon.build',
  role: 'Project Manager',
  unit: 'DoD',
  tenant: 'syncon.build',
  projects: ['Ft. Hood pkg 3', 'BRAC warehouse'],
  // Which specialists this user can call. Matches the prototype's
  // CURRENT_USER.specAccess — intentionally omits 'exec' so the
  // exec-gated tool (Portfolio Risk Scan) renders disabled.
  specAccess: ['pm', 'precon', 'accting', 'hr', 'knowledge', 'product'] as readonly string[],
} as const;

export const MOCK_PREFLIGHT: ToolResponse<PreflightAuthResult> = {
  success: true,
  tool: 'preflight_auth',
  count: 1,
  data: [
    {
      session_id: 'mock-session-0001',
      authorized: true,
      unrestricted: false,
      domain: 'coordinator',
      scope_type: 'project',
      accessible_ids: ['FH-03', 'BRAC-24'],
      effective_user_oid: MOCK_USER.oid,
      flags: {
        pilot: true,
        admin: false,
        manager: false,
      },
    },
  ],
};

/* A turn that produced an inline visual, reachable in e2e via the
   "[e2e:visual]" instruction marker. The markup is what an agent plausibly
   writes: it reads every figure off viz.data and hard-codes none, so it also
   stands as a worked example of the contract render_visual enforces. */
export const MOCK_RUN_AGENT_VISUAL: RunAgentResponse = {
  status: 'completed',
  agent_reply:
    'Billed ran ahead of cost in four of the six months; June is the one to look at.',
  tools_called: ['ask_executive_agent', 'performance_trend'],
  transcript_json: null,
  error_code: null,
  error: null,
  specialist_domain: 'exec',
  source_citations: [],
  artifacts: [
    {
      run_id: null,
      drive_id: null,
      drive_item_id: 'visual:e2e0000000000000',
      tool: 'render_visual',
      last_tool: 'render_visual',
      document_type_key: 'visual',
      document_title: 'Billed vs cost by month',
      output_format: 'visual',
      status: 'ready',
      file_url: null,
      sharepoint_path: null,
      embed_url: null,
      pending_job: null,
      visual: {
        visual_id: 'visual:e2e0000000000000',
        markup:
          "<svg id='c' viewBox='0 0 640 220' role='img' aria-label='Billed vs cost'></svg>" +
          '<script>(function(){' +
          'var rows = viz.data;' +
          "var y = viz.scaleLinear([0, viz.extent(rows,'billed')[1]], [200, 10]);" +
          "var x = viz.scaleBand(rows.map(function(r){return r.ym;}), [40, 620], 0.3);" +
          "var p = rows.map(function(r){" +
          "return \"<rect class='bar' x='\" + x(r.ym) + \"' y='\" + y(r.billed) +" +
          "\"' width='\" + x.bandwidth() + \"' height='\" + (y(0) - y(r.billed)) +" +
          "\"' fill='var(--accent)'/>\";" +
          '}).join("");' +
          "document.getElementById('c').innerHTML = p;" +
          '})();</script>',
        data: [
          { ym: '2026-01', billed: 1240000, cost: 1010000 },
          { ym: '2026-02', billed: 1850000, cost: 1402000 },
          { ym: '2026-03', billed: 910000, cost: 884000 },
          { ym: '2026-04', billed: 2310000, cost: 1760000 },
          { ym: '2026-05', billed: 1505000, cost: 1290000 },
          { ym: '2026-06', billed: 2020000, cost: 1455000 },
        ],
        sources: null,
        caption: 'performance_trend · as of 2026-09-21 09:15 · 6 months',
        drawn_by: 'executive',
      },
    },
  ],
};

export const MOCK_RUN_AGENT: RunAgentResponse = {
  status: 'completed',
  agent_reply:
    "Got it. I routed this to the PM specialist and they're looking at the Ft. Hood package 3 schedule. First-pass note: the MEP sequencing conflict on level 3 is the dominant risk. I'll have a full read in a moment.",
  tools_called: ['ask_pm_agent', 'project_health_status'],
  transcript_json: null,
  error_code: null,
  error: null,
  specialist_domain: 'pm',
  source_citations: [
    { kind: 'project', id: 'FH-03', label: 'Ft. Hood · Package 3' },
    { kind: 'project_doc', id: 'fh-spec26', label: 'Spec Div 26 · Electrical' },
  ],
  artifacts: [],
};
